import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Mail, Lock, AlertCircle, CheckCircle, Shield, Clock, Copy, Waves, ArrowLeft, Key, Eye, Bell, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { validateEmail } from '../../utils/turkishValidation';
import { supabase } from '../../lib/supabase';

export default function PasswordReset() {
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const { requestPasswordReset } = useAuth();

  const navigate = (path) => {
    window.location.href = path;
  };

  // Detect recovery token in URL hash or query params on mount
  useEffect(() => {
    const hash = window.location?.hash;
    const search = window.location?.search;

    // Supabase appends #access_token=...&type=recovery to the redirect URL
    if (hash) {
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      const type = hashParams?.get('type');
      const accessToken = hashParams?.get('access_token');
      if ((type === 'recovery' || accessToken) && type !== 'signup') {
        setStep('update');
        return;
      }
    }

    // Also check query string (some Supabase configs use ?type=recovery)
    if (search) {
      const queryParams = new URLSearchParams(search);
      const type = queryParams?.get('type');
      if (type === 'recovery') {
        setStep('update');
        return;
      }
    }

    // If navigated directly to /password-reset/update path without token,
    // check if Supabase already established a recovery session
    if (window.location?.pathname === '/password-reset/update') {
      supabase?.auth?.getSession()?.then(({ data: { session } }) => {
        if (session) {
          setStep('update');
        }
      });
    }
  }, []);

  const handleRequestReset = async (e) => {
    e?.preventDefault();
    
    if (!email) {
      setError('Lütfen e-posta adresinizi girin');
      return;
    }

    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: resetError } = await requestPasswordReset(email);

      if (resetError) {
        setError(resetError?.message);
      } else {
        setStep('confirmation');
        setEmail(email);
      }
    } catch (err) {
      setError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setResendCooldown(60);
    await handleRequestReset({ preventDefault: () => {} });
  };

  const handleUpdatePassword = async (e) => {
    e?.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    if (!Object.values(passwordStrength)?.every(val => val)) {
      setError('Lütfen tüm şifre gereksinimlerini karşılayın');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password: newPassword });
      if (updateError) {
        setError(updateError?.message || 'Şifre güncellenemedi. Lütfen tekrar deneyin.');
      } else {
        setStep('success');
      }
    } catch (err) {
      setError('Şifre güncellenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const copyErrorToClipboard = () => {
    navigator.clipboard?.writeText(error);
  };

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (step === 'success' && redirectCountdown > 0) {
      const timer = setTimeout(() => setRedirectCountdown(redirectCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === 'success' && redirectCountdown === 0) {
      navigate('/login');
    }
  }, [step, redirectCountdown]);

  useEffect(() => {
    setPasswordStrength({
      hasMinLength: newPassword?.length >= 8,
      hasUpperCase: /[A-Z]/?.test(newPassword),
      hasLowerCase: /[a-z]/?.test(newPassword),
      hasNumber: /[0-9]/?.test(newPassword),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/?.test(newPassword),
    });
  }, [newPassword]);

  return (
    <>
      <Helmet>
        <title>Şifre Sıfırlama - Paddle Türkiye</title>
        <meta name="description" content="Şifrenizi güvenli bir şekilde sıfırlayın" />
      </Helmet>
      <div
        className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
        style={{
          background: 'linear-gradient(135deg, #0d2233 0%, #1B4B66 35%, #2E7D8A 65%, #0f2d40 100%)',
        }}
      >
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
              <Waves size={26} color="#1B4B66" strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              PaddleTürkiye
            </h1>
          </div>
          <p className="text-white/75 text-sm">
            {step === 'request' && 'Şifrenizi sıfırlamak için e-posta adresinizi girin'}
            {step === 'confirmation' && 'Şifre sıfırlama bağlantısı gönderildi'}
            {step === 'update' && 'Yeni şifrenizi belirleyin'}
            {step === 'success' && 'Şifreniz başarıyla güncellendi'}
          </p>
        </div>

        {/* Form Card */}
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Card Header Strip */}
          <div
            className="px-8 py-5 flex items-center gap-3"
            style={{ background: 'linear-gradient(90deg, #1B4B66 0%, #2E7D8A 100%)' }}
          >
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
              {step === 'success' ? (
                <CheckCircle className="w-5 h-5 text-white" />
              ) : (
                <Shield className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-white font-semibold text-base leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {step === 'request' && 'Şifre Sıfırlama'}
                {step === 'confirmation' && 'E-posta Gönderildi'}
                {step === 'update' && 'Yeni Şifre Oluştur'}
                {step === 'success' && 'Şifre Güncellendi'}
              </h2>
              <p className="text-white/70 text-xs">Güvenli bağlantı ile korunmaktadır</p>
            </div>
          </div>

          {/* Card Body */}
          <div className="px-8 py-7">

            {/* Request Reset Form */}
            {step === 'request' && (
              <form onSubmit={handleRequestReset} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                    E-posta Adresi
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-slate-800 placeholder-slate-400 text-sm"
                      style={{ '--tw-ring-color': '#1B4B66' }}
                      onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px #1B4B6640'; e.target.style.borderColor = '#1B4B66'; }}
                      onBlur={(e) => { e.target.style.boxShadow = ''; e.target.style.borderColor = '#e2e8f0'; }}
                      placeholder="ornek@email.com"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-red-700">{error}</p>
                        <button
                          type="button"
                          onClick={copyErrorToClipboard}
                          className="mt-1.5 text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          Hata detaylarını kopyala
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-md hover:shadow-lg active:scale-[0.99]"
                  style={{ background: loading ? '#1B4B66cc' : 'linear-gradient(90deg, #1B4B66 0%, #2E7D8A 100%)' }}
                >
                  {loading ? 'Gönderiliyor...' : 'Sıfırlama Bağlantısı Gönder'}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 font-medium py-2 text-sm transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Giriş sayfasına dön
                </button>
              </form>
            )}

            {/* Email Confirmation */}
            {step === 'confirmation' && (
              <div className="space-y-5">
                <div className="text-center py-2">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: '#e8f0f5' }}>
                    <Mail className="w-8 h-8" style={{ color: '#1B4B66' }} />
                  </div>
                  <p className="text-slate-700 text-sm mb-3">
                    <strong className="text-slate-900">{email}</strong> adresine şifre sıfırlama bağlantısı gönderdik.
                  </p>
                  <p className="text-sm text-slate-500 mb-1">
                    E-postayı görmüyorsanız spam klasörünüzü kontrol edin.
                  </p>
                  <p className="text-xs text-slate-400">
                    Bağlantı 1 saat içinde geçersiz olacaktır.
                  </p>
                </div>

                <div className="border-t border-slate-100 pt-5">
                  <button
                    onClick={handleResendEmail}
                    disabled={resendCooldown > 0 || loading}
                    className="w-full font-medium py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 rounded-xl border transition-colors"
                    style={{ color: '#1B4B66', borderColor: '#1B4B6630' }}
                    onMouseEnter={(e) => { if (!e?.currentTarget?.disabled) e.currentTarget.style.background = '#f0f4fa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                  >
                    {resendCooldown > 0 ? (
                      <>
                        <Clock className="w-4 h-4" />
                        Tekrar gönder ({resendCooldown}s)
                      </>
                    ) : (
                      'E-postayı tekrar gönder'
                    )}
                  </button>
                </div>

                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 font-medium py-2 text-sm transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Giriş sayfasına dön
                </button>
              </div>
            )}

            {/* Update Password Form */}
            {step === 'update' && (
              <form onSubmit={handleUpdatePassword} className="space-y-5">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Yeni Şifre
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none"
                      onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px #1B4B6640'; e.target.style.borderColor = '#1B4B66'; }}
                      onBlur={(e) => { e.target.style.boxShadow = ''; e.target.style.borderColor = '#e2e8f0'; }}
                      placeholder="Yeni şifrenizi girin"
                      required
                    />
                  </div>
                </div>

                {newPassword && (
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <p className="text-xs font-medium text-slate-600 mb-2">Şifre Gereksinimleri:</p>
                    {[
                      { key: 'hasMinLength', text: 'En az 8 karakter' },
                      { key: 'hasUpperCase', text: 'Büyük harf (A-Z)' },
                      { key: 'hasLowerCase', text: 'Küçük harf (a-z)' },
                      { key: 'hasNumber', text: 'Rakam (0-9)' },
                      { key: 'hasSpecialChar', text: 'Özel karakter (!@#$...)' },
                    ]?.map(({ key, text }) => (
                      <div key={key} className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: passwordStrength?.[key] ? '#1B4B66' : '#cbd5e1' }}
                        >
                          {passwordStrength?.[key] && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <span className={`text-xs ${passwordStrength?.[key] ? 'text-blue-700' : 'text-slate-500'}`}>
                          {text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Şifre Tekrar
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none"
                      onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px #1B4B6640'; e.target.style.borderColor = '#1B4B66'; }}
                      onBlur={(e) => { e.target.style.boxShadow = ''; e.target.style.borderColor = '#e2e8f0'; }}
                      placeholder="Şifrenizi tekrar girin"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-red-700">{error}</p>
                        <button
                          type="button"
                          onClick={copyErrorToClipboard}
                          className="mt-1.5 text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          Hata detaylarını kopyala
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !Object.values(passwordStrength)?.every(val => val)}
                  className="w-full text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-md hover:shadow-lg active:scale-[0.99]"
                  style={{ background: 'linear-gradient(90deg, #1B4B66 0%, #2E7D8A 100%)' }}
                >
                  {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                </button>
              </form>
            )}

            {/* Success Message */}
            {step === 'success' && (
              <div className="py-2 space-y-5">
                {/* Success Icon & Message */}
                <div className="text-center">
                  <div
                    className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
                    style={{ background: 'linear-gradient(135deg, #e8f4f0 0%, #d1eae0 100%)', boxShadow: '0 0 0 8px #e8f4f020' }}
                  >
                    <CheckCircle className="w-10 h-10" style={{ color: '#1B8A5A' }} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Şifreniz Başarıyla Güncellendi!
                  </h3>
                  <p className="text-sm text-slate-600">
                    Yeni şifreniz güvenli bir şekilde kaydedildi. Artık hesabınıza giriş yapabilirsiniz.
                  </p>
                </div>

                {/* Auto-redirect countdown */}
                <div
                  className="flex items-center justify-center gap-3 rounded-xl px-4 py-3"
                  style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}
                >
                  <div className="relative flex items-center justify-center w-8 h-8">
                    <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                      <circle cx="16" cy="16" r="13" fill="none" stroke="#BFDBFE" strokeWidth="2.5" />
                      <circle
                        cx="16" cy="16" r="13" fill="none"
                        stroke="#1B4B66" strokeWidth="2.5"
                        strokeDasharray={`${2 * Math.PI * 13}`}
                        strokeDashoffset={`${2 * Math.PI * 13 * (1 - redirectCountdown / 5)}`}
                        style={{ transition: 'stroke-dashoffset 1s linear' }}
                      />
                    </svg>
                    <span className="absolute text-xs font-bold" style={{ color: '#1B4B66' }}>{redirectCountdown}</span>
                  </div>
                  <p className="text-sm font-medium" style={{ color: '#1B4B66' }}>
                    {redirectCountdown} saniye içinde giriş sayfasına yönlendiriliyorsunuz...
                  </p>
                </div>

                {/* Security Tips */}
                <div className="rounded-xl overflow-hidden border border-slate-100">
                  <div
                    className="px-4 py-3 flex items-center gap-2"
                    style={{ background: 'linear-gradient(90deg, #1B4B6610 0%, #2E7D8A10 100%)' }}
                  >
                    <Shield className="w-4 h-4" style={{ color: '#1B4B66' }} />
                    <span className="text-sm font-semibold" style={{ color: '#1B4B66' }}>Güvenlik İpuçları</span>
                  </div>
                  <div className="px-4 py-3 space-y-3">
                    {[
                      {
                        icon: <Key className="w-4 h-4 flex-shrink-0" style={{ color: '#2E7D8A' }} />,
                        text: 'Şifrenizi kimseyle paylaşmayın ve farklı sitelerde aynı şifreyi kullanmaktan kaçının.',
                      },
                      {
                        icon: <Eye className="w-4 h-4 flex-shrink-0" style={{ color: '#2E7D8A' }} />,
                        text: 'Hesabınıza yetkisiz erişim olduğunu düşünüyorsanız hemen şifrenizi değiştirin.',
                      },
                      {
                        icon: <Bell className="w-4 h-4 flex-shrink-0" style={{ color: '#2E7D8A' }} />,
                        text: 'Şüpheli giriş denemelerini fark ederseniz destek ekibimizle iletişime geçin.',
                      },
                    ]?.map((tip, i) => (
                      <div key={i} className="flex items-start gap-3">
                        {tip?.icon}
                        <p className="text-xs text-slate-600 leading-relaxed">{tip?.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Login Button */}
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-all text-sm shadow-md hover:shadow-lg active:scale-[0.99]"
                  style={{ background: 'linear-gradient(90deg, #1B4B66 0%, #2E7D8A 100%)' }}
                >
                  <LogIn className="w-4 h-4" />
                  Hemen Giriş Yap
                </button>
              </div>
            )}

            {/* SSL Trust Signal */}
            {(step === 'request' || step === 'update') && (
              <div className="mt-6 pt-5 border-t border-slate-100">
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                  <Shield className="w-3.5 h-3.5" />
                  <span>Verileriniz SSL ile korunmaktadır</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-white/40 text-xs text-center">
          &copy; {new Date()?.getFullYear()} PaddleTürkiye. Tüm hakları saklıdır.
        </p>
      </div>
    </>
  );
}